<template>
  <div v-if="isInTeam && isDataLoaded" class="dashboard">
    <Modal
      v-model="isChangePathwayModalOpen"
      title="Change Pathway?"
      ok-text="Yes"
      cancel-text="Cancel"
      ok-type="default"
      cancel-type="primary"
      @hide="handleChangePathwayHide"
      size="md"
    >
      <template #title>
        <h2 class="text-center">Change Pathway?</h2>
      </template>
      <p>
        Oh no! If this pathway doesn't suit your current team development then you can change the
        pathway for your team.
      </p>
      <p>
        Changing the Pathway for your team will affect all team members' current pathways. Click
        ‘Yes’ and you will be prompted to pick another pathway for your team.
      </p>
    </Modal>
    <section class="dashboard-background">
      <div class="container maxWidth-md text-big">
        <div class="row">
          <!-- PATHWAY PANEL -->
          <div class="col-sm-6 left-column">
            <DashboardPanel title="Our Pathway" :href="teamOutcomeTile.buttonUrl">
              <div class="outcome-panel-content team-outcome">
                <TileKebabMenu
                  data-test="kebab-menu"
                  v-if="
                    isTeamLeaderOrOrgAdmin &&
                    currentPathway?.cmsPathwayId &&
                    !currentPathway.completed
                  "
                >
                  <li>
                    <a
                      href="javascript:void(0)"
                      @click.stop.prevent="changePathway"
                      data-test="change-outcome"
                    >
                      Change pathway
                    </a>
                  </li>
                </TileKebabMenu>
                <img
                  alt="your pathway image"
                  data-test="outcome-tile-img"
                  :src="teamOutcomeTile.imgSrc"
                  class="outcome-tile-default-img"
                  width="80%"
                />
                <h3 data-test="outcome-tile-title">{{ teamOutcomeTile.panelTitle }}</h3>
                <p data-test="outcome-tile-subtitle" v-if="teamOutcomeTile.panelSubtitle">
                  {{ teamOutcomeTile.panelSubtitle }}
                </p>
                <Stepper
                  data-test="outcome-tile-progress"
                  v-if="currentPathway?.cmsPathwayId && userProgress && !currentPathway.completed"
                  :completed-step="completedStep"
                  :steps-count="pathwayStepCount"
                  :show-tooltip="true"
                />
                <button
                  style="margin-bottom: 10px"
                  data-test="outcome-tile-button"
                  class="btn btn-primary btn-large"
                  type="button"
                >
                  {{ teamOutcomeTile.buttonText }}
                </button>
              </div>
            </DashboardPanel>
          </div>
          <!-- END OF PATHWAY PANEL -->

          <div class="col-sm-6 right-column">
            <div class="row top-row">
              <!-- TEAM SOLO -->
              <div class="col-sm-6 panel-container">
                <DashboardPanel
                  :href="`/TeamReport/view/${id}`"
                  title="Team Solo"
                  tooltip-text="Our Team Solo shows all of our solo reports aggregated together. This is how our team views itself."
                  :is-clickable="!!(isSoloDiamondLoaded && hasTeamSoloReport)"
                >
                  <p v-if="isSoloDiamondLoaded && !hasTeamSoloReport">
                    Your team needs at least two members with Solo reports to have a Team report.
                  </p>
                  <div
                    v-else-if="!isSoloDiamondLoaded || hasTeamSoloReport"
                    :class="{ hidden: !isSoloDiamondLoaded || !hasTeamSoloReport }"
                    class="panel-head-container"
                  >
                    <TileKebabMenu ref="soloTileKebab" data-test="solo-kebab-menu"
                      ><li>
                        <a href="/resources/session/gyreinsights-team-solo-report"
                          ><span>GyreInsights: Team Solo report</span>
                          <span class="beta-sticker">BETA</span></a
                        >
                      </li></TileKebabMenu
                    >

                    <HeadFromModel
                      :animateEntrance="true"
                      :headId="id"
                      kind="teamsolo"
                      @onDataLoaded="soloDiamondLoaded"
                    />
                  </div>
                </DashboardPanel>
              </div>
              <!-- END OF TEAM SOLO -->

              <!-- TEAM VIEWPOINTS -->
              <div class="col-sm-6 panel-container">
                <DashboardPanel
                  title="Team Viewpoints"
                  :href="`/teamviewpointsreport/view/${id}`"
                  tooltip-text="Our Team Viewpoints report shows all our viewpoints feedback aggregated together. This is how others view our team."
                  :is-clickable="!!(isViewpointsDiamondLoaded && hasTeamViewpointsReport)"
                >
                  <p v-if="isViewpointsDiamondLoaded && !hasTeamViewpointsReport">
                    Your team needs at least two members with Viewpoints reports to have a Team
                    Viewpoints report.
                  </p>
                  <div
                    v-else-if="!isViewpointsDiamondLoaded || hasTeamViewpointsReport"
                    :class="{ hidden: !isViewpointsDiamondLoaded || !hasTeamViewpointsReport }"
                    class="panel-head-container"
                  >
                    <HeadFromModel
                      :animateEntrance="true"
                      :headId="id"
                      kind="teamviewpoints"
                      @onDataLoaded="viewpointsDiamondLoaded"
                    />
                  </div>
                </DashboardPanel>
              </div>
              <!-- END OF TEAM VIEWPOINTS -->
            </div>

            <div class="row bottom-row">
              <!-- TEAM EFFECTIVENESS -->
              <div class="col-sm-6 panel-container">
                <DashboardPanel
                  data-test="team-effectiveness-tile"
                  title="Team Effectiveness"
                  tooltip-text="Our Team Effectiveness Questionnaire displays our ratings of our team against Gyre’s 9 Questions Model."
                  :href="`/team/effectiveness/${id}`"
                >
                  <div
                    v-if="hasTeqReport"
                    class="panel-head-container teq-head-container"
                    data-test="has-teq-report-message"
                  >
                    <HeadFromModel
                      kind="TeamEffectivenessAggregateDiamond"
                      :headId="latestTeqReportId"
                      @onDataLoaded="teamEffectivenessLoaded"
                      :animateEntrance="true"
                    />
                  </div>
                  <p
                    v-else-if="latestTeqReport?.questionnaires?.length"
                    data-test="need-more-responses-message"
                    class="tile-message"
                  >
                    You need at least 2 responses to view the latest Team Effectiveness report.
                  </p>
                  <p v-else data-test="start-new-review-message" class="tile-message">
                    Your team can start a new Team Effectiveness Review at any time.
                  </p>
                </DashboardPanel>
              </div>
              <!-- END OF TEAM EFFECTIVENESS -->

              <!-- TEAM MEMBERS -->
              <div class="col-sm-6 panel-container">
                <DashboardPanel title="Team Members" :href="`/team/members/${id}`">
                  <img
                    class="team-member-icon"
                    alt="team members icon"
                    src="../../assets/team-members-icon.svg"
                  />
                </DashboardPanel>
              </div>
              <!-- END OF TEAM MEMBERS -->
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
  <div v-else-if="!isInTeam && isDataLoaded" class="dashboard">
    <section class="dashboard-background">
      <div class="container maxWidth-md text-big">
        <div class="no-team-container">
          <div class="no-team-img-container">
            <img src="../../assets/jyranians/gyranians-level-up.png" alt="level up" />
          </div>
          <div style="height: 20px" />
          <a class="btn btn-primary" href="/team/create/">Setup a new team</a>
        </div>
      </div>
    </section>
  </div>
  <LoadingPage v-else />
</template>

<script setup lang="ts">
import { debug as d } from "debug";
import axios from "axios";
import { Modal } from "uiv";
import { computed, onBeforeMount, ref } from "vue";
import {
  TeamApiModel,
  TeamCurrentAndPendingMembersApiModel,
  TeamEffectivenessReviewApiModel,
  TeamPathwayApiModel,
  TeamMemberCompletedPathwayStepApiModel,
  TeamSettingsApiModel,
} from "../../api-models";
import { settleAll } from "../../services/utilities/settle-all";
import LoadingPage from "../loading-page.vue";
import DashboardPanel from "../../components/dashboard-panel.vue";
import HeadFromModel from "../../components/head/head-from-model.vue";
import { csContext } from "../../services/cs-context";
import Stepper from "../../components/team-outcomes/progress-stepper.vue";
import cms from "../../services/cms/cms";
import { CmsTeamPathway } from "../../services/cms/cms-type-definitions";
import TileKebabMenu from "../../components/tile-kebab-menu.vue";

const debug = d("cs.TeamDashboardPage");

export interface TeamOutcomeTile {
  buttonUrl: string;
  imgSrc: string;
  buttonText: string;
  panelTitle: string;
  panelSubtitle: string;
}

const props = defineProps<{
  id: string;
  isTeamLeader: boolean;
  teamSettingsModel: TeamSettingsApiModel | null;
  teamModel: TeamApiModel | null;
}>();

const isInTeam = ref(false);
const isDataLoaded = ref(false);
const isSoloDiamondLoaded = ref(false);
const isViewpointsDiamondLoaded = ref(false);
const isTeamEffectivenessLoaded = ref(false);
const hasTeamSoloReport = ref<boolean | null>(null);
const hasTeamViewpointsReport = ref<boolean | null>(null);
const hasTeamEffectivenessData = ref<boolean | null>(null);
const latestTeqReport = ref<TeamEffectivenessReviewApiModel | null>(null);
const isChangePathwayModalOpen = ref(false);
const pathwayDoc = ref<CmsTeamPathway | null>(null);
const currentPathway = ref<TeamPathwayApiModel | null>(null);
const userProgress = ref<TeamMemberCompletedPathwayStepApiModel | null>(null);
const outcomeImgUrl = ref<string>();
const currentAndPendingMembers = ref<TeamCurrentAndPendingMembersApiModel | null>(null);
const hasTeamMemberCompletedPathway = ref<boolean | null>(null);

const teamOutcomeTile = computed(() => {
  return {
    panelSubtitle: "",
    panelTitle: "Browse pathways",
    buttonText: "Go",
    // eslint-disable-next-line global-require
    imgSrc: require("../../assets/jyranians-mountain.png"),
    buttonUrl: `/team/outcomes/${props.id}/select`,
  };
});

const hasTeqReport = computed(
  () => !!latestTeqReport.value && latestTeqReport.value?.questionnaires?.length > 1,
);

const latestTeqReportId = computed(() => latestTeqReport.value?.id);
const pathwayStepCount = computed(() => pathwayDoc.value?.data.pathway_steps.length ?? 0);
const completedStep = computed(() => userProgress.value?.stepIndex ?? -1);
const isTeamLeaderOrOrgAdmin = computed(
  () => props.isTeamLeader || csContext.userProfile.isOrgAdmin,
);

function soloDiamondLoaded(hasData: boolean): void {
  debug("soloDiamondLoaded", hasData);
  isSoloDiamondLoaded.value = true;
  hasTeamSoloReport.value = hasData;
}

function viewpointsDiamondLoaded(hasData: boolean): void {
  debug("viewpointsDiamondLoaded", hasData);
  isViewpointsDiamondLoaded.value = true;
  hasTeamViewpointsReport.value = hasData;
}

function teamEffectivenessLoaded(hasData: boolean): void {
  debug("teamEffectivenessLoaded", hasData);
  isTeamEffectivenessLoaded.value = true;
  hasTeamEffectivenessData.value = hasData;
}

async function setOutcomeUserProgressAndPathway(): Promise<void> {
  const resp = await axios.get(`/api/TeamPathway/${props.id}`);
  currentPathway.value = resp.data.teamPathway;
  userProgress.value = resp.data.userProgress;
  if (currentPathway.value) {
    pathwayDoc.value = await cms.getById("cms-pathway", currentPathway.value.cmsPathwayId);
    outcomeImgUrl.value = (
      await cms.getById("cms-goal", currentPathway.value.cmsOutcomeId)
    ).data.image.url;
  }
}

function isLastStepCompleted(): boolean {
  const currentPathwayId = currentPathway.value?.cmsPathwayId;
  if (currentPathwayId && pathwayDoc.value) {
    const pathwaySteps = pathwayDoc.value.data.pathway_steps;
    const lastPathwayStep = pathwaySteps[pathwaySteps.length - 1];
    const lastPathwayStepId = lastPathwayStep.pathway_step.id;
    // does the last step match the users current step and has it been completed
    return lastPathwayStepId === userProgress.value?.cmsStepId;
  }
  return false;
}

function setOutcomeTileContent(): void {
  if (isTeamLeaderOrOrgAdmin.value) teamOutcomeTile.value.panelTitle = "Choose pathway";

  if (currentPathway.value?.completed) return;

  const completedLastStep = isLastStepCompleted();

  if (currentPathway.value?.cmsOutcomeTitle && currentPathway.value?.cmsPathwayTitle) {
    teamOutcomeTile.value.panelTitle = currentPathway.value.cmsOutcomeTitle;
    teamOutcomeTile.value.panelSubtitle = currentPathway.value.cmsPathwayTitle;
    if (completedLastStep && !currentPathway.value.completed) {
      teamOutcomeTile.value.buttonUrl =
        hasTeamMemberCompletedPathway.value && pathwayDoc.value
          ? cms.linkResolver(pathwayDoc.value)
          : "/team/complete/";
      teamOutcomeTile.value.buttonText = isTeamLeaderOrOrgAdmin.value
        ? "Complete Pathway"
        : "Continue";
    } else {
      teamOutcomeTile.value.buttonUrl = pathwayDoc.value ? cms.linkResolver(pathwayDoc.value) : "";
      teamOutcomeTile.value.buttonText = "Continue";
    }
    teamOutcomeTile.value.imgSrc = outcomeImgUrl.value;
  }
}

async function hasUserCompletedPathway(): Promise<void> {
  const response = await axios.get(
    `/api/TeamMemberCompletedPathway/${currentPathway.value?.cmsPathwayId}/${props.id}`,
  );

  hasTeamMemberCompletedPathway.value = await response.data;

  const hasViewedCompletePage = userProgress.value?.hasViewedCompletePage;

  if (
    !hasTeamMemberCompletedPathway.value &&
    userProgress.value?.teamPathwayIsCurrent &&
    !hasViewedCompletePage
  ) {
    window.location.href = "/team/complete/";
  }
}

onBeforeMount(async () => {
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, 100);

  if (!props.id) {
    isDataLoaded.value = true;
    return;
  }

  isInTeam.value = true;

  const promises: Promise<void>[] = [];

  // promises.push(
  //   axios.get("/api/ReportPermissions/").then((resp) => {
  //     reportPermissionDialogModel.value = resp.data as ReportPermissionDialogModel;
  //   }),
  // );

  promises.push(
    axios.get(`/api/TeamMembership/${props.id}`).then((resp) => {
      currentAndPendingMembers.value = resp.data as TeamCurrentAndPendingMembersApiModel;
    }),
  );

  promises.push(
    axios.get(`/api/TeamEffectivenessReview/GetLatestForTeam?teamId=${props.id}`).then((resp) => {
      const data = resp.data as TeamEffectivenessReviewApiModel;
      if (data && data.id) {
        latestTeqReport.value = data;
      }
    }),
  );

  promises.push(setOutcomeUserProgressAndPathway());

  await settleAll(promises);
  setOutcomeTileContent();
  isDataLoaded.value = true;
  debug(csContext);

  if (currentPathway.value?.completed) {
    await hasUserCompletedPathway();
  }
});

function changePathway(): void {
  isChangePathwayModalOpen.value = true;
}

function handleChangePathwayHide(msg: "ok" | "cancel"): void {
  if (msg === "ok") {
    window.location.href = `/team/outcomes/${props.id}/select`;
  }
}
</script>

<style lang="less" scoped>
@import "../../styles/dashboards/dashboards.less";

.team-member-icon {
  width: 90%;

  @media (max-width: @screen-xs-max) {
    width: 50%;
  }
}

.text-wrap {
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
  white-space: nowrap;

  @media (max-width: @screen-sm-max) {
    max-width: 50vw;
  }

  @media (max-width: @screen-xxs-max) {
    max-width: 70vw;
  }
}

.no-team-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
}

.no-team-img-container {
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  width: 25%;
  aspect-ratio: 1;

  img {
    width: 140%;
    height: 140%;
    object-fit: cover;
    object-position: center;
  }

  @media (max-width: @screen-sm-max) {
    width: 40%;
  }

  @media (max-width: @screen-xxs-max) {
    width: 90%;
  }
}

.no-team-text {
  color: @form-text-color;
  max-width: 60%;
  padding-bottom: 2rem;
  text-align: center;

  @media (max-width: @screen-xs-max) {
    max-width: 90%;
  }
}
</style>
